import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, DollarSign, RefreshCw, CheckCircle, XCircle, Clock, Search, Filter, Loader2, Trash2, Archive, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);
  
  // Stripe config
  const [stripeConfig, setStripeConfig] = useState(null);
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(null);

  // Stats
  const [stats, setStats] = useState({
    total_expected: 0,
    total_paid: 0,
    total_refunded: 0,
    pending_count: 0,
    paid_count: 0
  });

  useEffect(() => {
    fetchData();
    fetchStripeConfig();
  }, [statusFilter]);

  useEffect(() => {
    setSelectedIds([]);
  }, [statusFilter]);

  const fetchStripeConfig = async () => {
    try {
      const response = await api.get('/admin/stripe-config');
      setStripeConfig(response.data);
    } catch (error) {
      console.error('Error fetching Stripe config:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch payment transactions
      const paymentsRes = await api.get('/admin/payments');
      let allPayments = paymentsRes.data || [];
      
      // Fetch bookings with deposits
      const bookingsRes = await api.get('/admin/bookings');
      const bookingsWithDeposit = bookingsRes.data.filter(b => b.deposit_amount > 0);
      setBookings(bookingsWithDeposit);
      
      // Filter payments
      let filtered = allPayments;
      if (statusFilter === 'pending') {
        filtered = allPayments.filter(p => p.status === 'pending' || p.payment_status === 'initiated');
      } else if (statusFilter === 'paid') {
        filtered = allPayments.filter(p => p.status === 'complete' || p.payment_status === 'paid');
      } else if (statusFilter === 'refunded') {
        filtered = allPayments.filter(p => p.status === 'refunded');
      }
      
      setPayments(filtered);

      // Calculate stats from bookings
      const paid = bookingsWithDeposit.filter(b => b.payment_status === 'caparra_pagata');
      const refunded = bookingsWithDeposit.filter(b => b.payment_status === 'rimborsato');
      const pending = bookingsWithDeposit.filter(b => b.payment_status !== 'caparra_pagata' && b.payment_status !== 'rimborsato' && b.status === 'confermata');
      
      setStats({
        total_expected: bookingsWithDeposit.reduce((acc, b) => acc + (b.deposit_amount || 0), 0),
        total_paid: paid.reduce((acc, b) => acc + (b.deposit_amount || 0), 0),
        total_refunded: refunded.reduce((acc, b) => acc + (b.deposit_amount || 0), 0),
        pending_count: pending.length,
        paid_count: paid.length
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // Bulk Actions
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredPayments.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredPayments.map(p => p.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const bulkDelete = async () => {
    setBulkLoading(true);
    try {
      const result = await api.post('/admin/payments/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} record eliminati (locale) - ${result.data.note || ''}`);
      setSelectedIds([]);
      setShowBulkConfirm(null);
      fetchData();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Errore nell\'eliminazione');
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkArchive = async () => {
    setBulkLoading(true);
    try {
      await api.post('/admin/payments/bulk-archive', { ids: selectedIds });
      toast.success(`${selectedIds.length} record archiviati`);
      setSelectedIds([]);
      setShowBulkConfirm(null);
      fetchData();
    } catch (error) {
      console.error('Bulk archive error:', error);
      toast.error('Errore nell\'archiviazione');
    } finally {
      setBulkLoading(false);
    }
  };

  const deletePayment = async (id) => {
    if (!window.confirm('Eliminare questo record pagamento?\n\nNOTA: Eliminazione LOCALE - lo storico Stripe NON viene modificato.')) return;
    try {
      await api.delete(`/admin/payments/${id}`);
      toast.success('Record eliminato (locale)');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getPaymentStatusBadge = (payment) => {
    if (payment.status === 'complete' || payment.payment_status === 'paid') {
      return <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">Pagato</span>;
    }
    if (payment.status === 'refunded') {
      return <span className="px-2 py-1 text-xs font-medium rounded bg-orange-100 text-orange-700">Rimborsato</span>;
    }
    if (payment.status === 'archiviato') {
      return <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">Archiviato</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700">In Attesa</span>;
  };

  const filteredPayments = payments.filter(payment => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      payment.stripe_session_id?.toLowerCase().includes(query) ||
      payment.metadata?.booking_code?.toLowerCase().includes(query) ||
      payment.metadata?.customer_email?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-payments-page">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl mb-2">Pagamenti</h1>
          <p className="text-muted-foreground">Gestisci le caparre e i rimborsi</p>
        </div>
        
        {/* Stripe Mode Badge */}
        {stripeConfig && (
          <div className="text-right">
            <Badge 
              variant={stripeConfig.is_live_mode ? "default" : "destructive"}
              className={`text-sm px-3 py-1 ${stripeConfig.is_live_mode ? 'bg-green-600' : 'bg-orange-500'}`}
            >
              {stripeConfig.mode === 'LIVE' ? '🟢 LIVE MODE' : '🟠 TEST MODE'}
            </Badge>
            <p className="text-xs text-muted-foreground mt-1">{stripeConfig.key_prefix}</p>
            {stripeConfig.warning && (
              <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700 flex items-center gap-1">
                <AlertTriangle size={14} />
                {stripeConfig.warning}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totale Previsto</p>
                <p className="text-2xl font-bold">€{stats.total_expected.toFixed(2)}</p>
              </div>
              <DollarSign className="text-muted-foreground" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Incassato</p>
                <p className="text-2xl font-bold text-green-700">€{stats.total_paid.toFixed(2)}</p>
              </div>
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50/50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Attesa</p>
                <p className="text-2xl font-bold text-yellow-700">{stats.pending_count}</p>
              </div>
              <Clock className="text-yellow-600" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rimborsato</p>
                <p className="text-2xl font-bold text-orange-700">€{stats.total_refunded.toFixed(2)}</p>
              </div>
              <RefreshCw className="text-orange-600" size={24} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca per codice, email..."
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Stato pagamento" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="pending">In Attesa</SelectItem>
            <SelectItem value="paid">Pagati</SelectItem>
            <SelectItem value="refunded">Rimborsati</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions Bar */}
      {filteredPayments.length > 0 && (
        <div className="flex items-center gap-4 mb-4 p-3 bg-muted/50 rounded">
          <Checkbox
            checked={selectedIds.length === filteredPayments.length && filteredPayments.length > 0}
            onCheckedChange={toggleSelectAll}
            data-testid="select-all-payments"
          />
          <span className="text-sm text-muted-foreground">
            {selectedIds.length > 0 ? `${selectedIds.length} selezionati` : 'Seleziona tutto'}
          </span>
          
          {selectedIds.length > 0 && (
            <div className="flex gap-2 ml-auto">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBulkConfirm('archive')}
                disabled={bulkLoading}
              >
                <Archive size={14} className="mr-1" />
                Archivia ({selectedIds.length})
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setShowBulkConfirm('delete')}
                disabled={bulkLoading}
              >
                <Trash2 size={14} className="mr-1" />
                Elimina ({selectedIds.length})
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Bulk Confirm Modal */}
      <Dialog open={!!showBulkConfirm} onOpenChange={() => setShowBulkConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {showBulkConfirm === 'delete' ? 'Conferma Eliminazione' : 'Conferma Archiviazione'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              Sei sicuro di voler {showBulkConfirm === 'delete' ? 'eliminare' : 'archiviare'}{' '}
              <strong>{selectedIds.length}</strong> record di pagamento?
            </p>
            {showBulkConfirm === 'delete' && (
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                <strong>⚠️ Eliminazione LOCALE</strong><br/>
                Lo storico su Stripe NON viene modificato. Questa operazione rimuove solo i record dal database locale.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkConfirm(null)}>Annulla</Button>
            <Button
              variant={showBulkConfirm === 'delete' ? 'destructive' : 'default'}
              onClick={showBulkConfirm === 'delete' ? bulkDelete : bulkArchive}
              disabled={bulkLoading}
            >
              {bulkLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
              {showBulkConfirm === 'delete' ? 'Elimina' : 'Archivia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payments Table */}
      <div className="bg-card border border-border overflow-hidden rounded">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 w-10"></th>
                <th className="text-left p-4 text-sm font-medium">Session ID</th>
                <th className="text-left p-4 text-sm font-medium">Prenotazione</th>
                <th className="text-left p-4 text-sm font-medium">Importo</th>
                <th className="text-left p-4 text-sm font-medium">Stato</th>
                <th className="text-left p-4 text-sm font-medium">Data</th>
                <th className="text-left p-4 text-sm font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment) => (
                <tr
                  key={payment.id}
                  className="border-t border-border hover:bg-muted/30 transition-colors"
                >
                  <td className="p-4">
                    <Checkbox
                      checked={selectedIds.includes(payment.id)}
                      onCheckedChange={() => toggleSelect(payment.id)}
                    />
                  </td>
                  <td className="p-4 font-mono text-xs">
                    {payment.stripe_session_id?.slice(0, 20)}...
                    {payment.is_mock && <Badge variant="outline" className="ml-2 text-xs">Mock</Badge>}
                  </td>
                  <td className="p-4">
                    <div className="font-medium">{payment.metadata?.booking_code || 'N/A'}</div>
                    <div className="text-sm text-muted-foreground">{payment.metadata?.customer_email || ''}</div>
                  </td>
                  <td className="p-4 font-medium">€{payment.amount?.toFixed(2)}</td>
                  <td className="p-4">{getPaymentStatusBadge(payment)}</td>
                  <td className="p-4 text-sm text-muted-foreground">
                    {payment.created_at ? new Date(payment.created_at).toLocaleString('it-IT') : 'N/A'}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deletePayment(payment.id)}
                        title="Elimina (locale)"
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPayments.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            {payments.length === 0 ? 'Nessun pagamento registrato' : 'Nessun risultato per i filtri applicati'}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
        <strong>ℹ️ Nota:</strong> L&apos;eliminazione dei record pagamento è solo locale e non modifica lo storico su Stripe. 
        Usa questa funzione per pulire dati di test.
      </div>
    </div>
  );
}
